import { createContext, useCallback, useContext, useEffect, useState } from "react";
import ComposeDialog from "@/components/dashboard/ComposeDialog";
import api from "@/lib/api";

const ComposeContext = createContext(null);

/** useCompose().openCompose({to, subject, body}) opens the shared compose
 *  dialog on top of whatever tab the user is currently viewing. */
export function useCompose() {
  const ctx = useContext(ComposeContext);
  if (!ctx) throw new Error("useCompose must be used inside <ComposeProvider>");
  return ctx;
}

export function ComposeProvider({ children }) {
  const [accounts, setAccounts] = useState([]);
  const [composeData, setComposeData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/email/accounts");
        if (alive) setAccounts(Array.isArray(data) ? data : []);
      } catch (e) {
        /* not connected yet – ok */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const openCompose = useCallback(
    ({ to = "", subject = "", body = "", cc = "", files = [] } = {}) => {
      const acc = accounts[0]?.email;
      if (!acc) {
        // No mailbox connected yet – fall back to opening the phone's mail
        // client via a mailto: link so the user isn't stuck.
        const q = new URLSearchParams();
        if (subject) q.set("subject", subject);
        if (body) q.set("body", body);
        if (cc) q.set("cc", cc);
        const qs = q.toString();
        window.location.href = `mailto:${to}${qs ? `?${qs}` : ""}`;
        return;
      }
      setComposeData({
        mode: "new",
        account: acc,
        to,
        cc,
        subject,
        body,
        thread_id: null,
        files,
      });
    },
    [accounts],
  );

  return (
    <ComposeContext.Provider value={{ openCompose }}>
      {children}
      {composeData && (
        <ComposeDialog
          data={composeData}
          accounts={accounts}
          onClose={() => setComposeData(null)}
          onSent={() => setComposeData(null)}
        />
      )}
    </ComposeContext.Provider>
  );
}
